import React from "react";
import { motion } from "framer-motion";
import { MapPin, ArrowRight, Bed, Bath, Maximize2 } from "lucide-react";
import { useNavigate } from "react-router-dom";

const PropertyCard = ({
  status,
  image,
  title,
  price,
  location,
  sqft,
  beds,
  baths,
  details,
  slug,
  externalLink
}) => {
  const navigate = useNavigate();

  const handleClick = () => {
    if (externalLink) {
      window.open(externalLink, "_blank", "noopener,noreferrer");
    } else {
      navigate(`/property/${slug}`);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.6 }}
      onClick={handleClick}
      className="group relative flex flex-col h-full bg-white rounded-3xl border border-[#E8E3DC] p-5 cursor-pointer shadow-md hover:shadow-2xl hover:-translate-y-1 transition-all duration-500 font-sans"
    >
      {/* Image Container */}
      <div className="relative aspect-[16/11] overflow-hidden mb-6 rounded-2xl bg-gray-100">
        <img
          src={image || "https://images.unsplash.com/photo-1512917774080-9991f1c4c750"}
          alt={title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
        />

        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-ink-900/60 via-transparent to-transparent opacity-60 group-hover:opacity-40 transition-opacity" />

        {/* Status Badge */}
        {status && (
          <div className="absolute top-4 left-4">
            <span className="bg-ink-900/90 backdrop-blur-md border border-white/20 text-white px-3.5 py-1.5 text-[10px] tracking-widest uppercase font-bold rounded-full font-display">
              {status}
            </span>
          </div>
        )}

        {/* Floating Explore Badge */}
        <div className="absolute bottom-4 right-4 translate-y-2 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-300">
          <div className="flex items-center gap-1.5 bg-tropical-500 text-white px-4 py-2 text-[11px] font-bold uppercase tracking-wider rounded-full shadow-lg">
            Explore <ArrowRight className="w-3.5 h-3.5" />
          </div>
        </div>
      </div>

      {/* Content Area */}
      <div className="flex flex-col flex-grow px-2">
        <div className="mb-4">
          <div className="flex justify-between items-start gap-4 mb-2">
            <h3 className="text-xl font-bold text-ink-900 font-display leading-tight group-hover:text-tropical-600 transition-colors">
              {title}
            </h3>
          </div>

          <div className="flex justify-between items-center mt-2">
            <p className="text-2xl font-extrabold text-brand-dark font-display">
              {price}
            </p>
          </div>

          <p className="flex items-center gap-1.5 text-ink-500 text-xs tracking-wider uppercase font-semibold mt-3">
            <MapPin className="w-3.5 h-3.5 text-tropical-500 shrink-0" />{" "}
            {location || "St. John, USVI"}
          </p>
        </div>

        {/* Footer Specs */}
        <div className="mt-auto pt-4 border-t border-gray-100 flex justify-between items-center">
          {details ? (
            <div className="space-y-0.5">
              <p className="text-[10px] text-tropical-500 uppercase tracking-widest font-bold">Specs</p>
              <p className="text-xs font-semibold text-ink-800 uppercase">{details}</p>
            </div>
          ) : (
            <div className="flex gap-6">
              {beds && beds !== "0" && (
                <div className="flex items-center gap-1.5">
                  <Bed className="w-4 h-4 text-tropical-500" />
                  <span className="text-xs font-bold text-ink-800">{beds} Beds</span>
                </div>
              )}
              {baths && baths !== "0" && (
                <div className="flex items-center gap-1.5">
                  <Bath className="w-4 h-4 text-tropical-500" />
                  <span className="text-xs font-bold text-ink-800">{baths} Baths</span>
                </div>
              )}
              {sqft && sqft !== "N/A" && (
                <div className="flex items-center gap-1.5">
                  <Maximize2 className="w-3.5 h-3.5 text-tropical-500" />
                  <span className="text-xs font-bold text-ink-800">
                    {isNaN(sqft) ? sqft : Number(sqft).toLocaleString()}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default PropertyCard;
